import { useState } from "react";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { money } from "@/data/products";
import { CheckoutDialog } from "@/components/CheckoutDialog";

const Line = ({ l }) => {
  const { setQty } = useCart();
  const key = l.key;
  return (
    <li data-testid={`cart-line-${l.product_id}`} className="flex gap-4 py-4 border-b border-[#181203]/15">
      <div className="w-16 h-20 rounded-xl grid place-items-center shrink-0 bg-[#E5A910]">
        <img src={l.product.img ?? "/img/bottle_pineapple.webp"} alt="" className="h-16 object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold">{l.product.name}</p>
        {l.addonLabels.length > 0 && <p className="text-xs text-[#594519]">{l.addonLabels.join(" · ")}</p>}
        <p className="text-xs text-[#594519]">{money(l.unit)} each</p>
        <div className="mt-2 flex items-center gap-2">
          <button data-testid={`cart-dec-${l.product_id}`} onClick={() => setQty(key, l.qty - 1)} className="w-8 h-8 rounded-full border border-[#181203]/40 grid place-items-center hover:bg-[#181203]/10"><Minus className="w-3 h-3" /></button>
          <span data-testid={`cart-qty-${l.product_id}`} className="w-6 text-center font-bold">{l.qty}</span>
          <button data-testid={`cart-inc-${l.product_id}`} onClick={() => setQty(key, l.qty + 1)} className="w-8 h-8 rounded-full border border-[#181203]/40 grid place-items-center hover:bg-[#181203]/10"><Plus className="w-3 h-3" /></button>
          <button data-testid={`cart-remove-${l.product_id}`} onClick={() => setQty(key, 0)} className="ml-auto text-[#594519] hover:text-[#B5122F]" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <span className="font-bold">{money(l.total)}</span>
    </li>
  );
};

export const CartDrawer = () => {
  const { open, setOpen, lines, subtotal } = useCart();
  const [checkout, setCheckout] = useState(false);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="cart-drawer" className="bg-[#FFF4D6] text-[#181203] w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="font-display text-3xl font-extrabold">Your cart</SheetTitle>
            <SheetDescription data-testid="cart-delivery-note" className="inline-flex items-center gap-2 text-[#594519]">
              <Truck className="w-4 h-4" /> Delivery is quoted separately by text.
            </SheetDescription>
          </SheetHeader>
          <ul className="flex-1 overflow-y-auto" data-lenis-prevent>
            {lines.length === 0 && <p data-testid="cart-empty" className="py-16 text-center text-[#594519]">Your cart is empty.</p>}
            {lines.map((l) => <Line key={l.key} l={l} />)}
          </ul>
          <div className="border-t border-[#181203]/20 pt-4 space-y-3">
            <div className="flex justify-between font-display text-xl font-bold">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <button
              data-testid="cart-checkout-button"
              disabled={!lines.length}
              onClick={() => { setOpen(false); setCheckout(true); }}
              className="w-full h-14 rounded-full bg-[#181203] text-[#FFD000] font-bold hover:bg-black disabled:opacity-40 transition-colors"
            >
              Checkout
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <CheckoutDialog open={checkout} onOpenChange={setCheckout} />
    </>
  );
};
